import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import {
  CheckCircle2Icon,
  ListTodoIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  RotateCcwIcon,
  Trash2Icon,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { ApiError } from '@/api/errors'
import { invalidateTaskViews } from '@/api/invalidate'
import { queryKeys } from '@/api/keys'
import { tasksApi } from '@/api/tasks'
import type { TaskDetail, UpdateTaskPayload } from '@/api/types'
import { ActivityFeed } from '@/components/activity-feed'
import { TeamAttachments } from '@/components/attachments/attachment-gallery'
import { ClientLink } from '@/components/client-link'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { DeletedRecordNotice } from '@/components/deleted-record-notice'
import { SourceDocument } from '@/components/documents/source-document'
import { EmptyState } from '@/components/empty-state'
import { SourceInteraction } from '@/components/interactions/source-excerpt'
import { Markdown } from '@/components/markdown'
import { MarkdownEditor } from '@/components/markdown-editor'
import { ReferencedBy } from '@/components/references/referenced-by'
import { CloseTaskDialog } from '@/components/tasks/close-task-dialog'
import { InlineTitle } from '@/components/tasks/inline-title'
import { ParentTaskLink } from '@/components/tasks/parent-task-link'
import { ClientRequestBadge } from '@/components/tasks/task-badges'
import { TaskComments } from '@/components/tasks/task-comments'
import { TaskControlItems } from '@/components/tasks/task-control-items'
import { AddDependencyButton, TaskDependencyList } from '@/components/tasks/task-dependencies'
import { TaskProperties } from '@/components/tasks/task-properties'
import { TaskSubtasks } from '@/components/tasks/task-subtasks'
import { TaskTimeSection } from '@/components/time/task-time-section'
import { StartTimerButton } from '@/components/time/timer-widget'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { UserAvatar } from '@/components/user-avatar'
import { useGoBackTo, useReturnTo } from '@/hooks/use-return-to'
import { useSessionChoice } from '@/hooks/use-session-choice'
import { useFollowClient } from '@/lib/current-client'
import { formatDateTime, formatDuration } from '@/lib/format'
import { isClosedStatus } from '@/lib/labels'
import { deletionLoss } from '@/lib/task-deletion'

export const Route = createFileRoute('/_app/tasks/$taskKey')({
  component: TaskDetailPanel,
})

const SIDE_TABS = ['proprietes', 'temps', 'activite'] as const
type SideTab = (typeof SIDE_TABS)[number]

function TaskDetailPanel() {
  const { taskKey } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const router = useRouter()
  const retour = useReturnTo()
  const goBackTo = useGoBackTo()
  const queryClient = useQueryClient()

  const [description, setDescription] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)
  /**
   * Conservé d'une tâche à l'autre le temps de la session : passer en revue le
   * temps de dix tâches ne doit pas coûter dix clics. Oublié à la fermeture de
   * l'onglet, pour que la visite suivante reparte des Propriétés.
   */
  const [sideTab, setSideTab] = useSessionChoice<SideTab>(
    'oo.tache.onglet-lateral',
    SIDE_TABS,
    'proprietes',
  )

  const taskQuery = useQuery({
    queryKey: queryKeys.tasks.detail(taskKey),
    queryFn: () => tasksApi.get(taskKey),
  })
  // #285 : le client courant du menu devient celui de la fiche.
  useFollowClient(taskQuery.data?.client_id)

  const refresh = () => invalidateTaskViews(queryClient)

  /**
   * Fermer le panneau ramène à la page d'origine — l'onglet Tâches d'une fiche
   * client, par exemple — plutôt que systématiquement à la liste globale.
   * En accès direct (aucun historique), on retombe sur `/tasks`.
   */
  /**
   * **Une seule fermeture par ouverture.** Deux Échap rapprochés — le second
   * tapé pendant que le panneau s'efface — appelaient `close` deux fois, donc
   * reculaient de **deux** pas dans l'historique : on retombait sur une entrée
   * plus ancienne (une tâche déjà vue, qui semblait alors refuser de se fermer),
   * et un recul arrivé après un nouveau clic annulait ce clic. Observé par
   * oo-master sur `E2EOOM05-1`, reproduit et mesuré ici.
   */
  const closing = useRef<string | null>(null)
  const close = () => {
    if (closing.current === taskKey) return
    closing.current = taskKey
    // Ouvert depuis une fiche (#224) : on y revient, même onglet, mêmes
    // filtres — et après un rechargement, quand l'historique ne sait plus.
    if (retour) {
      goBackTo(retour)
      return
    }
    if (router.history.canGoBack()) {
      router.history.back()
      return
    }
    void navigate({ to: '/tasks', search })
  }

  const update = useMutation({
    mutationFn: (payload: UpdateTaskPayload) => tasksApi.update(taskKey, payload),
    // Les changements de propriété se voient immédiatement ; la réponse du
    // serveur fait autorité, et un échec restaure l'état précédent.
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tasks.detail(taskKey) })
      const previous = queryClient.getQueryData<TaskDetail>(queryKeys.tasks.detail(taskKey))

      if (previous) {
        queryClient.setQueryData<TaskDetail>(queryKeys.tasks.detail(taskKey), {
          ...previous,
          ...payload,
        })
      }
      return { previous }
    },
    onError: (error, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.tasks.detail(taskKey), context.previous)
      }
      toast.error(error instanceof ApiError ? error.message : 'Modification impossible.')
    },
    onSettled: () => {
      void refresh()
    },
  })

  const closeTask = useMutation({
    mutationFn: (comment: string | undefined) => tasksApi.close(taskKey, comment),
    onSuccess: (task) => {
      void refresh()
      toast.success(`${task.key} terminée.`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Fermeture impossible.')
    },
    onSettled: () => {
      setCloseOpen(false)
    },
  })

  const reopenTask = useMutation({
    mutationFn: () => tasksApi.reopen(taskKey, 'todo'),
    onSuccess: (task) => {
      void refresh()
      toast.success(`${task.key} rouverte.`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Réouverture impossible.')
    },
  })

  const restoreTask = useMutation({
    mutationFn: () => tasksApi.restore(taskKey),
    onSuccess: () => {
      void invalidateTaskViews(queryClient)
      toast.success(
        'Tâche restaurée. Ses sous-tâches et ses dépendances, elles, ne reviennent pas.',
      )
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Restauration impossible.')
    },
  })

  const removeTask = useMutation({
    mutationFn: () => tasksApi.remove(taskKey),
    onSuccess: () => {
      void refresh()
      toast.success(`${taskKey} supprimée.`)
      close()
    },
    onError: (error) => {
      toast.error(
        error instanceof ApiError && error.isForbidden
          ? 'Seuls le rapporteur de la tâche et un administrateur peuvent la supprimer.'
          : error instanceof ApiError
            ? error.message
            : 'Suppression impossible.',
      )
    },
    onSettled: () => {
      setConfirmDelete(false)
    },
  })

  const activity = useQuery({
    queryKey: [...queryKeys.tasks.detail(taskKey), 'activity'],
    queryFn: () => tasksApi.activity(taskKey, { page_size: 30 }),
    enabled: taskQuery.isSuccess,
  })

  const task = taskQuery.data
  const isClosed = task ? isClosedStatus(task.status) : false
  const descriptionDraft = description ?? task?.description ?? ''
  const descriptionDirty = description !== null && description !== (task?.description ?? '')

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) close()
      }}
    >
      {/* `@container` et non les points de rupture d'écran : la mise en page
          dépend de la largeur du panneau, pas de celle de la fenêtre. */}
      {/*
        Plus large qu'avant (768 px) parce que la colonne de droite porte
        désormais le Temps et l'Activité : des phrases complètes et des saisies
        datées, pas seulement des sélecteurs courts. Borné à 5xl pour que la
        liste reste lisible derrière, au moins ses clés et ses titres.
      */}
      <SheetContent side="right" className="@container w-full gap-0 overflow-y-auto sm:max-w-5xl">
        {taskQuery.isPending ? (
          <div className="space-y-4 p-5">
            <SheetTitle className="sr-only">Chargement de {taskKey}</SheetTitle>
            <SheetDescription className="sr-only">
              Les informations de la tâche sont en cours de chargement.
            </SheetDescription>
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : taskQuery.isError ? (
          <div className="p-5">
            <SheetTitle className="sr-only">Tâche introuvable</SheetTitle>
            <EmptyState
              icon={ListTodoIcon}
              title={
                taskQuery.error instanceof ApiError && taskQuery.error.isNotFound
                  ? `Tâche ${taskKey} introuvable`
                  : 'Impossible de charger cette tâche'
              }
              description={
                taskQuery.error instanceof ApiError
                  ? taskQuery.error.message
                  : 'Réessayez dans un instant.'
              }
              action={
                <Button variant="outline" onClick={close}>
                  Retour aux tâches
                </Button>
              }
            />
          </div>
        ) : task ? (
          <>
            <header className="space-y-3 border-b p-5">
              <div className="flex flex-wrap items-center gap-2">
                <code className="font-key rounded bg-muted px-1.5 py-0.5 text-muted-foreground">
                  {task.key}
                </code>
                <ClientLink
                  client={task.client}
                  clientId={task.client_id}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  {task.client.name}
                </ClientLink>
                <ClientRequestBadge clientRequest={task.client_request} />
                {task.client_visible && <Badge variant="info">Visible client</Badge>}

                <div className="ml-auto flex items-center gap-1.5">
                  {/* Une tâche supprimée se lit, ne s'écrit plus : l'API refuse
                      l'écriture en 404 (contrat 1.35.8). */}
                  {!task.deleted_at && <AddDependencyButton task={task} />}
                  {!task.deleted_at && (
                    <StartTimerButton taskId={task.id} taskKey={task.key} size="icon-sm" />
                  )}
                  {!task.deleted_at &&
                    (isClosed ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={reopenTask.isPending}
                        onClick={() => {
                          reopenTask.mutate()
                        }}
                      >
                        {reopenTask.isPending ? (
                          <Loader2Icon className="animate-spin" />
                        ) : (
                          <RotateCcwIcon />
                        )}
                        Rouvrir
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={closeTask.isPending}
                        onClick={() => {
                          setCloseOpen(true)
                        }}
                      >
                        {closeTask.isPending ? (
                          <Loader2Icon className="animate-spin" />
                        ) : (
                          <CheckCircle2Icon />
                        )}
                        Terminer
                      </Button>
                    ))}

                  {!task.deleted_at && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label="Actions sur la tâche">
                          <MoreHorizontalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem disabled>
                          Créée {formatDateTime(task.created_at)}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => {
                            setConfirmDelete(true)
                          }}
                        >
                          <Trash2Icon />
                          Supprimer la tâche
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>

              <DeletedRecordNotice
                deletedAt={task.deleted_at}
                what="Cette tâche"
                pending={restoreTask.isPending}
                caveat={
                  <>
                    La restauration ne ramène que la tâche : ses sous-tâches restent à la corbeille
                    et se reprennent une à une, et ses dépendances sont à reposer à la main —
                    supprimer les avait retranchées, rien ne les retrouve.
                  </>
                }
                onRestore={() => {
                  restoreTask.mutate()
                }}
              />

              <ParentTaskLink task={task} />

              <SheetTitle asChild>
                <div>
                  <InlineTitle
                    value={task.title}
                    disabled={update.isPending}
                    onSave={(title) => {
                      update.mutate({ title })
                    }}
                  />
                </div>
              </SheetTitle>

              <SheetDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <UserAvatar user={task.reporter} className="size-4" />
                  Créée par {task.reporter.name}
                </span>
                {task.time_seconds_total > 0 && (
                  <span>Temps passé : {formatDuration(task.time_seconds_total)}</span>
                )}
              </SheetDescription>

              <SourceInteraction
                interactionId={task.source_interaction_id}
                excerpt={task.source_excerpt}
              />
              <SourceDocument clientId={task.client_id} document={task.source_document} />

              {/* Sous le titre : dit tout de suite si l'on peut commencer. */}
              <TaskDependencyList task={task} />
            </header>

            <div className="grid gap-5 p-5 @4xl:grid-cols-[minmax(0,1fr)_26rem]">
              <div className="min-w-0 space-y-5">
                <section className="space-y-2">
                  <h3 className="text-sm font-medium">Description</h3>
                  {description === null ? (
                    <button
                      type="button"
                      className="w-full rounded-md p-2 text-left transition-colors hover:bg-accent/40 focus-visible:outline-2 focus-visible:outline-ring"
                      onClick={() => {
                        setDescription(task.description ?? '')
                      }}
                    >
                      {task.description ? (
                        <Markdown>{task.description}</Markdown>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Ajouter une description (Markdown accepté)…
                        </span>
                      )}
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <MarkdownEditor
                        value={descriptionDraft}
                        onChange={setDescription}
                        rows={6}
                        references={{
                          clientId: task.client_id,
                          clientVisible: task.client_visible,
                        }}
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDescription(null)
                          }}
                        >
                          Annuler
                        </Button>
                        <Button
                          size="sm"
                          disabled={!descriptionDirty || update.isPending}
                          onClick={() => {
                            update.mutate(
                              { description: descriptionDraft.trim() || null },
                              {
                                onSuccess: () => {
                                  setDescription(null)
                                },
                              },
                            )
                          }}
                        >
                          {update.isPending && <Loader2Icon className="animate-spin" />}
                          Enregistrer
                        </Button>
                      </div>
                    </div>
                  )}
                </section>

                {task.attachments.length > 0 && (
                  <section className="space-y-2" data-testid="pieces-demande">
                    <h3 className="text-sm font-medium">Pièces jointes par le client</h3>
                    <TeamAttachments attachments={task.attachments} />
                  </section>
                )}

                <TaskControlItems
                  items={task.control_items}
                  projectRef={task.project?.ref ?? null}
                />

                <section className="space-y-2">
                  <h3 className="flex items-center gap-2 text-sm font-medium">
                    Sous-tâches
                    {task.subtasks_count > 0 && (
                      <Badge variant="muted" className="tabular-nums">
                        {task.subtasks_done_count}/{task.subtasks_count}
                      </Badge>
                    )}
                  </h3>
                  <TaskSubtasks task={task} />
                </section>

                {/* Seul contenu restant de l'ancien groupe d'onglets : sans
                    voisin, un onglet n'est plus un choix, c'est un détour. */}
                <section className="space-y-2">
                  <h3 className="flex items-center gap-2 text-sm font-medium">
                    Commentaires
                    {task.comments_count > 0 && (
                      <Badge variant="muted" className="tabular-nums">
                        {task.comments_count}
                      </Badge>
                    )}
                  </h3>
                  <TaskComments
                    taskKey={task.key}
                    clientId={task.client_id}
                    clientVisible={task.client_visible}
                  />
                </section>

                <ReferencedBy type="task" id={task.id} bare />
              </div>

              <aside className="min-w-0 @4xl:border-l @4xl:pl-5">
                <Tabs
                  value={sideTab}
                  onValueChange={(value) => {
                    setSideTab(value as SideTab)
                  }}
                >
                  <TabsList className="w-full">
                    <TabsTrigger value="proprietes" className="flex-1">
                      Propriétés
                    </TabsTrigger>
                    <TabsTrigger value="temps" className="flex-1">
                      Temps
                    </TabsTrigger>
                    <TabsTrigger value="activite" className="flex-1">
                      Activité
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="proprietes" className="pt-3">
                    <TaskProperties
                      task={task}
                      disabled={update.isPending}
                      onChange={(payload) => {
                        update.mutate(payload)
                      }}
                    />
                  </TabsContent>
                  <TabsContent value="temps" className="pt-3">
                    <TaskTimeSection task={task} />
                  </TabsContent>
                  <TabsContent value="activite" className="pt-3">
                    <ActivityFeed
                      items={activity.data?.items ?? []}
                      isPending={activity.isPending}
                      emptyTitle="Aucune activité"
                    />
                  </TabsContent>
                </Tabs>
              </aside>
            </div>

            <CloseTaskDialog
              open={closeOpen}
              onOpenChange={setCloseOpen}
              taskKey={task.key}
              clientVisible={task.client_visible}
              pending={closeTask.isPending}
              onConfirm={(comment) => {
                closeTask.mutate(comment)
              }}
            />

            <ConfirmDialog
              open={confirmDelete}
              title={`Supprimer ${task.key} ?`}
              description={
                // Dire la perte **avant** : après, il n'y a plus rien à décider.
                <span className="space-y-2">
                  <span className="block">
                    La tâche, ses commentaires et ses saisies de temps ne seront plus accessibles.
                    Elle restera lisible depuis la corbeille, et se restaure.
                  </span>
                  {deletionLoss(task).map((phrase) => (
                    <span key={phrase} className="block" data-testid="perte-suppression">
                      {phrase}
                    </span>
                  ))}
                </span>
              }
              confirmLabel="Supprimer"
              destructive
              pending={removeTask.isPending}
              onOpenChange={(open) => {
                if (!open && !removeTask.isPending) setConfirmDelete(false)
              }}
              onConfirm={() => {
                removeTask.mutate()
              }}
            />
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
